import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import './style.scss';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import AdminPageLayout from '../../../layout/AdminPageLayout';
import Headline from '../../../ui/Headline';
import Button from '../../../ui/Button';
import ButtonsGroup from '../../../ui/ButtonsGroup';
import Loader from '../../../ui/Loader';
import PlusIcon from '../../../assets/icons/plus.svg';
import AdminFaqList from '../../../modules/AdminFaqList';
import FaqCategoryEditForm from '../../../modules/AdminFaqList/FaqCategoryEditForm';
import FaqItemsSection from '../../../modules/AdminFaqList/FaqItemsSection';
import { useAppDispatch } from '../../../store';
import { setIsModalOpen, setModalContent, setModalContentProps, } from '../../../store/slices/global';
import { MODAL_CONTENT } from '../../../app.config.vars/modalContent';
import { getAdminFaqCategories, sortFaqCategories } from '../../../api/admin/faq';
import { selectAdminFaqCategories, selectAdminFaqLoadStatus, selectCategorySortingDirty, setAdminFaqCategories, setCategorySorting, setCategorySortingDirty, setLoadStatus, upsertCategory, } from '../../../store/adminSlices/faq';
// Phase 6 D-10 + D-12 + D-14 + D-15 + D-18 + UI-SPEC § Layout Contract:
//
// Replaces the 06-01 placeholder. Orchestrates the categories layer of the
// FAQ admin UI:
//   * Mount-fetch the trilingual tree (D-18: one call, three languages — no
//     re-fetch on i18n.language change).
//   * Render categories via AdminFaqList with reorder + edit + delete + add.
//   * Hand FaqCategoryEditForm into AdminFaqList for the in-place edit slot
//     (when editingCategoryUid === c.uid) per D-12.
//   * Render a bottom-of-list FaqCategoryEditForm for the [+ Add category]
//     flow (D-12 recommended bottom anchor).
//   * Dirty-tracked save-order button (D-14, D-15 — independent of items).
//   * Delete-category opens the dedicated 409-aware modal (D-24); 06-04
//     plugs the items layer in via the renderItemsSlot render prop below.
const FaqAdminEmptyState = () => {
    const { t } = useTranslation();
    return (_jsxs("div", { className: "faq-empty-state", children: [_jsx(Headline, { title: t('admin.faq.empty.heading'), type: 'h2' }), _jsx("p", { children: t('admin.faq.empty.body') })] }));
};
const ManageFaqPage = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const categories = useSelector(selectAdminFaqCategories);
    const loadStatus = useSelector(selectAdminFaqLoadStatus);
    const categorySortingDirty = useSelector(selectCategorySortingDirty);
    const [editingCategoryUid, setEditingCategoryUid] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [saveSortingStatus, setSaveSortingStatus] = useState(null);
    const createFormRef = useRef(null);
    useEffect(() => {
        if (showCreateForm) {
            createFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [showCreateForm]);
    // D-18: one mount-time fetch. The trilingual tree already contains all three
    // languages embedded per row, so we deliberately depend on [dispatch] only —
    // language switches do NOT re-fetch.
    useEffect(() => {
        const fetchTree = async () => {
            dispatch(setLoadStatus('pending'));
            try {
                const tree = await getAdminFaqCategories();
                dispatch(setAdminFaqCategories(tree.categories));
                dispatch(setLoadStatus('success'));
            }
            catch {
                dispatch(setLoadStatus('error'));
                dispatch(setIsModalOpen(true));
                dispatch(setModalContent(MODAL_CONTENT.somethingWentWrong.key));
            }
        };
        fetchTree();
    }, [dispatch]);
    const handleAddCategory = () => {
        setShowCreateForm(true);
    };
    const handleCreateSaved = (saved) => {
        dispatch(upsertCategory(saved));
        setShowCreateForm(false);
    };
    const handleEditSaved = (saved) => {
        dispatch(upsertCategory(saved));
        setEditingCategoryUid(null);
    };
    const handleSortChanged = (orderedUids) => {
        dispatch(setCategorySorting(orderedUids));
        dispatch(setCategorySortingDirty(true));
    };
    const handleSaveCategorySorting = async () => {
        if (!categories)
            return;
        setSaveSortingStatus('pending');
        try {
            await sortFaqCategories(categories.map((c) => c.uid));
            dispatch(setCategorySortingDirty(false));
        }
        catch {
            dispatch(setIsModalOpen(true));
            dispatch(setModalContent(MODAL_CONTENT.somethingWentWrong.key));
        }
        finally {
            // WR-01: only the 'pending' state has observable effect (disables the
            // save button); 'success' / 'error' would be clobbered by this finally
            // block in the same micro-task so they never reach a render. Reset
            // directly to null.
            setSaveSortingStatus(null);
        }
    };
    const openDeleteCategoryModal = (uid) => {
        dispatch(setIsModalOpen(true));
        dispatch(setModalContent(MODAL_CONTENT.confirmDeleteFaqCategory.key));
        dispatch(setModalContentProps({ categoryUid: uid }));
    };
    const isCategoryEditing = (c) => editingCategoryUid === c.uid;
    const renderCategoryEditForm = (c) => (_jsx(FaqCategoryEditForm, { category: c, onCancel: () => setEditingCategoryUid(null), onSaved: handleEditSaved }));
    const renderableCategories = categories ?? [];
    const showLoader = loadStatus === 'pending' || categories === null;
    const showEmpty = !showLoader && loadStatus === 'success' && renderableCategories.length === 0 && !showCreateForm;
    const showList = !showLoader && loadStatus === 'success' && (renderableCategories.length > 0 || showCreateForm);
    return (_jsx(AdminPageLayout, { logoutButton: true, children: _jsxs("div", { className: 'page-content-wrapper manage-faq-page', children: [_jsx(Headline, { title: t('admin.faq.headline'), type: 'h1' }), _jsx(ButtonsGroup, { children: _jsx(Button, { onClick: handleAddCategory, label: t('admin.faq.add_category'), icon: _jsx(PlusIcon, {}), iconSize: 'small', disabled: showCreateForm }) }), categorySortingDirty && (_jsx(ButtonsGroup, { children: _jsx(Button, { label: t('button.save_changes'), onClick: handleSaveCategorySorting, disabled: saveSortingStatus === 'pending' }) })), showLoader && _jsx(Loader, {}), showEmpty && _jsx(FaqAdminEmptyState, {}), showList && (_jsxs(_Fragment, { children: [_jsx(Headline, { title: t('admin.faq.your_faq'), type: 'h2' }), _jsx(AdminFaqList, { categories: renderableCategories, onSortChanged: handleSortChanged, onEditCategory: (uid) => setEditingCategoryUid(uid), onDeleteCategory: openDeleteCategoryModal, isCategoryEditing: isCategoryEditing, renderCategoryEditForm: renderCategoryEditForm, renderItemsSlot: (c) => _jsx(FaqItemsSection, { category: c }) })] })), showCreateForm && (_jsx("div", { ref: createFormRef, children: _jsx(FaqCategoryEditForm, { onCancel: () => setShowCreateForm(false), onSaved: handleCreateSaved }) }))] }) }));
};
export default ManageFaqPage;
